import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Settings } from 'lucide-react';
import Header from '../../../components/ui/Header';
import Sidebar from '../../../components/ui/Sidebar';
import { usePermissions } from '../../../contexts/PermissionContext';

// Tab components
import CompanyProfileTab from './components/CompanyProfileTab';
import InvoiceSettingsTab from './components/InvoiceSettingsTab';
import JobConfigurationTab from './components/JobConfigurationTab';
import NotificationPreferencesTab from './components/NotificationPreferencesTab';
import TeamPermissionsTab from './components/TeamPermissionsTab';
import SecurityAccountTab from './components/SecurityAccountTab';

/**
 * Corporate Settings Page
 * Comprehensive settings interface with 6 tabs:
 * 1. Company Profile - Logo, address, contact, business hours, branding
 * 2. Invoice Settings - Invoice prefix, payment terms, footer, tax rate
 * 3. Job Configuration - Job ID preferences, job templates
 * 4. Notifications - Personal notification preferences
 * 5. Team & Permissions - Team summary, permission overview
 * 6. Security & Account - User profile, password, sessions, data export
 *
 * Navigation is now handled via the main Sidebar with expandable sub-items.
 */
const CorporateSettings = () => {
  const [searchParams] = useSearchParams();
  const { hasPermission, hasAnyPermission, userRole, loading: permLoading } = usePermissions();

  // Primary owner and owner always have full access
  const isOwner = userRole === 'primary_owner' || userRole === 'owner';

  // Get tab from URL or default to 'profile'
  const activeTab = searchParams.get('tab') || 'profile';

  // Tab permissions configuration
  const tabPermissions = {
    profile: null, // All company members can view
    invoice: 'manage_invoice_settings',
    jobs: { permissions: ['manage_jobs', 'manage_job_templates'], type: 'any' },
    notifications: null, // Personal settings, always visible
    permissions: null, // Personal settings - view own permissions
    security: null // Personal settings, always visible
  };

  // Check if user can see a tab
  const canSeeTab = (tabId) => {
    // Owner roles always have full access to all tabs
    if (isOwner) return true;

    const permission = tabPermissions[tabId];
    if (permission === null) return true;

    if (typeof permission === 'object') {
      if (permission.type === 'any') {
        return hasAnyPermission(permission.permissions);
      }
      return permission.permissions.every(p => hasPermission(p));
    }

    return hasPermission(permission);
  };

  // Tab titles for the header
  const tabTitles = {
    profile: 'Company Profile',
    invoice: 'Invoice Settings',
    jobs: 'Job Configuration',
    notifications: 'Notifications',
    permissions: 'Permissions',
    security: 'Security & Account'
  };

  const tabDescriptions = {
    profile: 'Manage your company logo, address, and contact information',
    invoice: 'Configure invoice prefix, payment terms, and footer',
    jobs: 'Set up job ID preferences and templates',
    notifications: 'Manage your email, SMS, and push notification preferences',
    permissions: 'View your role and permissions in this company',
    security: 'Update your profile, password, and manage sessions'
  };

  if (permLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  // If user doesn't have permission for the current tab, show access denied
  if (!canSeeTab(activeTab)) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar />
        <main className="ml-0 lg:ml-60 mt-[73px] min-h-screen">
          <div className="p-6 lg:p-8">
            <div className="bg-surface border border-border rounded-xl p-8 text-center">
              <Settings size={48} className="mx-auto text-text-tertiary mb-4" />
              <h2 className="text-xl font-semibold text-text-primary mb-2">Access Restricted</h2>
              <p className="text-text-secondary">
                You don't have permission to view this settings section.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <CompanyProfileTab />;
      case 'invoice':
        return <InvoiceSettingsTab />;
      case 'jobs':
        return <JobConfigurationTab />;
      case 'notifications':
        return <NotificationPreferencesTab />;
      case 'permissions':
        return <TeamPermissionsTab />;
      case 'security':
        return <SecurityAccountTab />;
      default:
        return <CompanyProfileTab />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar />

      <main className="ml-0 lg:ml-60 mt-[73px] min-h-screen">
        <div className="p-6 lg:p-8">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Settings size={20} color="white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-text-primary">{tabTitles[activeTab] || 'Settings'}</h1>
                <p className="text-text-secondary text-sm">
                  {tabDescriptions[activeTab] || 'Manage your company settings'}
                </p>
              </div>
            </div>
          </div>

          {/* Settings Content */}
          <div className="bg-surface border border-border rounded-xl p-6">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CorporateSettings;
